import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import SearchBox from '../SearchBox';
import {
  INSTRUMENT_FAMILIES,
  MIDI_INSTRUMENTS,
  getFamilyLabel,
  getInstrumentName,
  getInstrumentSizeKb,
  isSampledInstrument,
} from '../../../utils/midiInstruments';
import { hasFinePointer } from '../../../utils/platform';

import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as StopIcon } from '../../../icons/square-rounded.svg';

import './InstrumentBrowser.scss';

/**
 * The whole General MIDI set, grouped and searchable. The body of InstrumentRow's sheet.
 *
 * Viewers get no picker at all, only the name of what the admin settled on, so nothing here has to
 * double as a compact control.
 *
 * Rows follow AssignPartModal's list: a name, a check on the selected one, and a tap target that
 * is the whole row rather than a control inside it.
 */
// What the category row sits on when nothing is narrowed down
const ALL_FAMILIES = 'all';

const InstrumentBrowser = ({
  value,
  onSelect,
  defaultId,
  downloadedIds,
  onPreview,
  playingId,
  loadingId,
  progress,
}) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [family, setFamily] = useState(ALL_FAMILIES);

  // getInstrumentSizeKb walks a preset's range every call, and this list is 126 long — cheap
  // individually, but not worth redoing on every keystroke of the search box
  const rows = useMemo(
    () =>
      MIDI_INSTRUMENTS.map((instrument) => ({
        instrument,
        name: getInstrumentName(instrument),
        familyLabel: getFamilyLabel(instrument.family),
        sizeKb: isSampledInstrument(instrument) ? getInstrumentSizeKb(instrument) : 0,
      })),
    // The names are in the app's language
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const query = search.trim().toLowerCase();
  const isFiltered = family !== ALL_FAMILIES;

  // Category and search compose rather than override each other — picking Guitar and typing
  // "clean" means the clean guitar, not every clean-sounding thing in the set
  const matches = rows.filter(({ instrument, name, familyLabel }) => {
    if (isFiltered && instrument.family !== family) return false;
    if (!query) return true;

    // Family matches too, so "guitar" finds the whole family and "pad" finds the pads — the
    // label is the only place that word appears for a row like Warm Pad. The English name still
    // matches in Spanish, since that's what many musicians call the sound.
    return [name, instrument.name, familyLabel].some((text) => text.toLowerCase().includes(query));
  });

  // The list is already in GM program order, so families arrive contiguous and grouping is a
  // single pass rather than a sort. Narrowing to one category makes the heading say what the
  // selected chip already says, so it is dropped in that case.
  const groups = [];

  matches.forEach((row) => {
    const last = groups[groups.length - 1];

    if (last && last.family === row.instrument.family) last.rows.push(row);
    else groups.push({ family: row.instrument.family, label: isFiltered ? '' : row.familyLabel, rows: [row] });
  });

  return (
    <div className="InstrumentBrowser">
      <SearchBox
        value={search}
        label={t('instruments.search')}
        // Desktop only: on a phone or tablet it would put the keyboard over half the sheet
        autoFocus={hasFinePointer}
        onChange={setSearch}
        onClearInput={() => setSearch('')}
      />

      {/* Scrolls sideways rather than wrapping: sixteen categories would otherwise stack into
          four rows on a phone and push the list itself off the screen */}
      <div className="categories" role="tablist" aria-label={t('instruments.category')}>
        {[{ family: ALL_FAMILIES }, ...INSTRUMENT_FAMILIES].map(({ family: id }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={family === id}
            className={family === id ? 'category selected' : 'category'}
            onClick={() => setFamily(id)}
          >
            {id === ALL_FAMILIES ? t('common.all') : getFamilyLabel(id)}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <p className="empty">{t('instruments.noMatch')}</p>
      ) : (
        <div className="groups">
          {groups.map(({ family, label, rows: familyRows }) => (
            <section key={family} className="group">
              {label && <h4 className="group-title">{label}</h4>}
              <ul className="list">
                {familyRows.map(({ instrument, name, sizeKb }) => {
                  const selected = instrument.id === value;
                  const playing = playingId === instrument.id;
                  const loading = loadingId === instrument.id;

                  return (
                    <li
                      key={instrument.id}
                      className={selected ? 'item selected' : 'item'}
                      onClick={() => onSelect(instrument.id)}
                    >
                      <span className="name">{name}</span>
                      {instrument.id === defaultId && <span className="default-tag">{t('player.default')}</span>}

                      {/* The bar takes the size label's place and its width, so the row doesn't
                          reflow when a download starts. Checked before `selected` because the
                          instrument downloading is always the selected one — testing selection
                          first would mean the bar never appeared at all. */}
                      {loading && progress?.total > 0 ? (
                        <span
                          className="progress"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={progress.total}
                          aria-valuenow={progress.loaded}
                          aria-label={t('instruments.downloading', { name })}
                        >
                          <span
                            className="fill"
                            style={{ width: `${Math.round((progress.loaded / progress.total) * 100)}%` }}
                          />
                        </span>
                      ) : selected ? (
                        <CheckIcon className="check" />
                      ) : (
                        // Rendered even when there is no size to show — an instrument already
                        // downloaded still has to hold the slot open, or its play button would
                        // sit where the next row's size label is
                        <span className="size">
                          {sizeKb > 0 && !downloadedIds?.has(instrument.id) ? `${sizeKb} KB` : ''}
                        </span>
                      )}
                      {/* Only when there is a clip to play it with — the icon would otherwise
                          promise something the sheet cannot deliver */}
                      {onPreview && (
                        <button
                          type="button"
                          className={playing ? 'preview playing' : 'preview'}
                          aria-label={t(playing ? 'instruments.stop' : 'instruments.play', { name })}
                          disabled={loading}
                          // The row selects on click; without this, previewing would fire that
                          // too as the event bubbled up through it
                          onClick={(event) => {
                            event.stopPropagation();
                            onPreview(instrument.id);
                          }}
                        >
                          {playing ? <StopIcon /> : <PlayIcon />}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

InstrumentBrowser.propTypes = {
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  /** Marked Default, so there is somewhere obvious to come back to */
  defaultId: PropTypes.string,
  downloadedIds: PropTypes.instanceOf(Set),
  // Given only when a clip is attached — its absence is what hides the per-row play buttons,
  // since there would be nothing for them to play
  onPreview: PropTypes.func,
  /** The instrument sounding right now, if any */
  playingId: PropTypes.string,
  /** The instrument being prepared — downloading its samples, or building its voice */
  loadingId: PropTypes.string,
  /** How far `loadingId`'s download has got, in files. Drives the bar that replaces its size. */
  progress: PropTypes.shape({ loaded: PropTypes.number, total: PropTypes.number }),
};

export default InstrumentBrowser;
